import {
  Modal,
  ModalBody,
  ModalContent,
  ModalHeader,
  Spinner,
  Table,
  TableBody,
  TableCell,
  TableColumn,
  TableHeader,
  TableRow,
} from "@heroui/react";
import { useEffect, useState } from "react";
import {
  ContractContact,
  ContractMilestoneDate,
  ContractPhase,
  ContactType,
  MilestoneDateType,
} from "shared/types/rental-contracts";
import { UnitDTO } from "shared/types/unit";
import { Flag, Check, ArrowSquareOut, Calendar, X, Info } from "@phosphor-icons/react";
import { fetchWithAuth } from "shared/api/fetch-with-auth";
import { API_URL } from "constants/index";
import { useSession } from "@clerk/clerk-react";
import { formatDate } from "shared/utils/get-dates";
import { CustomToggle } from "shared/components/ui/CustomToggle";
import { UnitCommercialInfoComponent } from "shared/components/unit/UnitCommercialInfoComponent";
import { TimeslotModal } from "../components/TimeslotModal";
import { ContactLinkCell } from "../components/ContactLinkCell";
import { BrokerListCell } from "../components/BrokerListCell";
import { UnitIdLinkChip } from "../components/UnitIdLinkChip";
import { CandidatesLinkCell } from "../components/CandidatesLinkCell";
import { EditableCurrencyCell } from "../components/EditableCurrencyCell";
import { PublicatieCell } from "../components/PublicatieCell";
import { getUpcomingVacancyMetrics } from "shared/utils/vacancy-metrics";
import { Question } from "@phosphor-icons/react";
import {
  Tooltip as ShadTooltip,
  TooltipTrigger as ShadTooltipTrigger,
  TooltipContent as ShadTooltipContent,
  TooltipProvider as ShadTooltipProvider,
} from "components/ui/tooltip";

export interface UpcomingVacantUnitsTableProps {
  upcomingVacantUnits: UnitDTO[];
  fetchUpcomingVacantUnits: () => Promise<void>;
  fetchUnitsInRenovation: () => Promise<void>;
 }

const UpcomingVacantUnitsTable = ({ upcomingVacantUnits, fetchUpcomingVacantUnits, fetchUnitsInRenovation }: UpcomingVacantUnitsTableProps) => {
  const [units, setUnits] = useState<UnitDTO[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isTimeslotModalOpen, setIsTimeslotModalOpen] = useState<boolean>(false);
  const [selectedUnit, setSelectedUnit] = useState<UnitDTO | null>(null);
  const { session } = useSession();
  const [isCommercialInfoModalOpen, setIsCommercialInfoModalOpen] = useState<boolean>(false);
  const [selectedCommercialInfoUnit, setSelectedCommercialInfoUnit] = useState<UnitDTO | null>(null);

  useEffect(() => {
    setUnits(upcomingVacantUnits);
    setIsLoading(false);
  }, [upcomingVacantUnits]);

  // Helper function to determine the 'Einddatum' for a unit
  const getUnitEndDate = (unit: UnitDTO) => {
    if (unit.contracts && Array.isArray(unit.contracts)) {
      // Find contract in termination phase
      const terminationContract = unit.contracts.find(
        contract => contract.phase === ContractPhase.TERMINATION_PHASE
      );

      if (terminationContract?.contractMilestoneDates) {
        const endDates = terminationContract.contractMilestoneDates.filter(
          (milestone: ContractMilestoneDate) =>
            milestone.milestoneDateType === MilestoneDateType.END_DATE &&
            milestone.date
        );

        if (endDates.length > 0) {
          // Get the most recent end date
          const endDate = endDates.sort(
            (a: any, b: any) => new Date(b.date!).getTime() - new Date(a.date!).getTime()
          )[0];

          return formatDate(new Date(endDate.date!));
        }
      }
    }

    return "Geen einddatum gevonden";
  };

  const calculateERVPerM2 = (unit: UnitDTO) => {
    if (!unit.estimatedRentalValue || !unit.netSurface) return "N/A";
    return `€ ${(unit.estimatedRentalValue / unit.netSurface).toFixed(2)}`;
  };

  const getUnitTenant = (unit: UnitDTO) => {
    if (unit.contracts && Array.isArray(unit.contracts)) {
      const terminationContract = unit.contracts.find(
        (contract: any) => contract.phase === ContractPhase.TERMINATION_PHASE
      );

      if (terminationContract?.contractContacts) {
        const tenantContact = terminationContract.contractContacts.find(
          (cc: ContractContact) => cc.contactType === ContactType.TENANT
        );

        if (tenantContact?.resource?.name) {
          return tenantContact.resource.name;
        }
      }
    }

    return "—";
  };

  const handleUpdateUnit = async (unitId: string, updates: any) => {
    try {
      await fetchWithAuth(
        `${API_URL}/units/update/${unitId}`,
        {
          method: "PUT",
          body: JSON.stringify({ id: unitId, ...updates }),
          headers: {
            'Content-Type': 'application/json'
          }
        },
        session
      );
      // If the renovation status changed, refresh all related tables
      if ('vacantDueToRenovation' in updates) {
        // Fetch data for all tables that share units
        fetchUpcomingVacantUnits();
        fetchUnitsInRenovation();
      } else {
        // Otherwise just refresh this table
        fetchUpcomingVacantUnits();
      }

      // Update local state if we have the unit in our list
      setUnits(prevUnits => 
        prevUnits.map(unit => 
          unit.id === unitId ? { ...unit, ...updates } : unit
        )
      );
    } catch (error) {
      console.error("Failed to update unit:", error);
    }
  };

  const handleCloseTimeslotModal = () => {
    setIsTimeslotModalOpen(false);
    setSelectedUnit(null);
  };

  const handleUnitUpdate = (unitId: string, updates: Partial<UnitDTO>) => {
    setUnits(prevUnits => 
      prevUnits.map(unit => 
        unit.id === unitId ? { ...unit, ...updates } : unit
      )
    );
  };

  const handleOpenCommercialInfoModal = (unit: UnitDTO) => {
    setSelectedCommercialInfoUnit(unit);
    setIsCommercialInfoModalOpen(true);
  };

  const handleCloseCommercialInfoModal = () => {
    setIsCommercialInfoModalOpen(false);
    setSelectedCommercialInfoUnit(null);
  };

  const handleCommercialInfoUpdate = (unitId: string, updates: Partial<UnitDTO>) => {
    setUnits(prevUnits => 
      prevUnits.map(unit => 
        unit.id === unitId ? { ...unit, ...updates } : unit
      )
    );
    
    // Also update the selected unit if it's the same
    if (selectedCommercialInfoUnit?.id === unitId) {
      setSelectedCommercialInfoUnit(prev => prev ? { ...prev, ...updates } : null);
    }
  };

  const handleImmowebReferenceUpdate = async (unitId: string, referenceId: number | null) => {
    try {
      const response = await fetchWithAuth(
        `${API_URL}/units/${unitId}/reference-id`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ referenceId }),
        },
        session
      );

      if (response.ok) {
        const updatedUnit = await response.json();
        // Update local state
        setUnits(prevUnits => 
          prevUnits.map(unit => 
            unit.id === unitId ? { 
              ...unit, 
              immowebReferenceId: updatedUnit.immowebReferenceId,
              immowebReferenceIdAddedTimestamp: updatedUnit.immowebReferenceIdAddedTimestamp
            } : unit
          )
        );
        
        if (referenceId) {
          console.log('Immoweb referentie succesvol bijgewerkt!');
        } else {
          console.log('Immoweb referentie succesvol verwijderd!');
        }
      }
    } catch (error) {
      console.error('Error updating Immoweb reference:', error);
    }
  };

  return (
    <div id="units-vacant" className="col-span-full flex flex-col gap-2">
      <header className="flex items-center justify-between">
        <div className="flex items-center gap-1 text-lg font-medium">
          <Flag weight="duotone" size={20} />
          Aankomende leegstand
        </div>
        <div className="flex items-center gap-2">
          <a
            className="rounded-full border border-neutral-primary/20 px-3 py-1.5 text-sm font-medium text-moss-primary"
            href="https://web.whise.eu/login.html"
            target="_blank"
          >
            Whise
          </a>
        </div>
      </header>
      <div className="flex flex-col rounded-lg bg-white shadow-sm">
        {isLoading ? (
          <div className="flex h-full items-center justify-center">
            <Spinner
              label="Gegevens aan het ophalen"
              color="success"
              size="md"
            />
          </div>
        ) : (
          <Table
            aria-label="Aankomende leegstand"
            classNames={{
              wrapper: "shadow-none rounded-lg p-0 overflow-x-auto",
              thead: "h-fit border-b-2 border-neutral-primary/40",
              th: "rounded-none! bg-white py-1.5 px-2 h-fit text-xs uppercase tracking-wide text-neutral-primary font-semibold whitespace-nowrap",
              tbody: "border-b divide-y divide-neutral-primary/15",
              tr: "divide-x divide-neutral-primary/15",
              td: "py-1.5 px-2 align-top text-sm",
            }}
          >
            <TableHeader>
              <TableColumn>Unit</TableColumn>
              <TableColumn>Einddatum</TableColumn>
              <TableColumn>Dagen tot einde</TableColumn>
              <TableColumn>Huidige huurder</TableColumn>
              <TableColumn>Kandidaten</TableColumn>
              <TableColumn>Kalender</TableColumn>
              <TableColumn>Publicatie</TableColumn>
              <TableColumn>Makelaar</TableColumn>
              <TableColumn>Bezichtigings.</TableColumn>
              <TableColumn>Huur</TableColumn>
              <TableColumn>Prov.</TableColumn>
              <TableColumn>
                <div className="flex items-center gap-1">
                  ERV
                  <ShadTooltipProvider>
                    <ShadTooltip>
                      <ShadTooltipTrigger asChild>
                        <span className="inline-flex cursor-help text-neutral-secondary-mid">
                          <Question size={14} weight="duotone" />
                        </span>
                      </ShadTooltipTrigger>
                      <ShadTooltipContent side="top" className="max-w-xs">
                        <p className="text-xs">
                          Dit is de geschatte huur, dit is niet per se gelijk aan de
                          geafficheerde huur. ERV = Estimated Rental Value en dit is de
                          waarde die we intern denken te halen.
                        </p>
                      </ShadTooltipContent>
                    </ShadTooltip>
                  </ShadTooltipProvider>
                </div>
              </TableColumn>
              <TableColumn>ERV/m²</TableColumn>
              <TableColumn>ERV prov.</TableColumn>
              <TableColumn>Voorz.</TableColumn>
              <TableColumn>Renovatie</TableColumn>
            </TableHeader>
            <TableBody>
              {units.map((unit: UnitDTO) => {
                const upcoming = getUpcomingVacancyMetrics(unit);
                const days = upcoming.daysUntilEnd;
                return (
                <TableRow key={unit.id}>
                  {/* Unit ID */}
                  <TableCell>
                    <UnitIdLinkChip
                      unitId={unit.id as string}
                      unitType={unit.unitType}
                      unitSubtype={unit.unitSubtype}
                    />
                  </TableCell>

                  {/* Einddatum */}
                  <TableCell>
                    <span className="text-neutral-primary">{getUnitEndDate(unit)}</span>
                  </TableCell>

                  {/* Dagen tot einde */}
                  <TableCell>
                    {days === null ? (
                      <span className="text-neutral-secondary-mid">—</span>
                    ) : (
                      <span
                        className={`font-medium ${
                          days <= 30
                            ? "text-red-600"
                            : days <= 60
                              ? "text-amber-600"
                              : "text-neutral-primary"
                        }`}
                      >
                        {days === 0 ? "Vandaag" : `${days} dagen`}
                      </span>
                    )}
                  </TableCell>

                  {/* Huidige huurder */}
                  <TableCell>
                    <span className="text-neutral-primary">{getUnitTenant(unit)}</span>
                  </TableCell>

                  {/* Kandidaten */}
                  <TableCell>
                    <CandidatesLinkCell
                      unitId={unit.id as string}
                      count={unit.applicationStats?.activeCount ?? unit.applicationStats?.total ?? 0}
                    />
                  </TableCell>

                  {/* Kalender */}
                  <TableCell>
                    <div className="flex items-center gap-1.5">
                      <ShadTooltipProvider>
                        <ShadTooltip>
                          <ShadTooltipTrigger asChild>
                            <div
                              className={`flex h-6 w-6 items-center justify-center rounded-full ${
                                unit.visibleInCalendar
                                  ? "bg-status-good/10 text-status-good"
                                  : "bg-red-50 text-red-500"
                              }`}
                              aria-label={
                                unit.visibleInCalendar
                                  ? "Zichtbaar op afsprakenkalender"
                                  : "Niet zichtbaar op afsprakenkalender"
                              }
                            >
                              {unit.visibleInCalendar ? (
                                <Check size={14} weight="bold" />
                              ) : (
                                <X size={14} weight="bold" />
                              )}
                            </div>
                          </ShadTooltipTrigger>
                          <ShadTooltipContent side="top">
                            <p className="text-xs">
                              {unit.visibleInCalendar
                                ? "Zichtbaar op afsprakenkalender"
                                : "Niet zichtbaar op afsprakenkalender"}
                            </p>
                          </ShadTooltipContent>
                        </ShadTooltip>
                      </ShadTooltipProvider>
                      <a
                        href={`/unit/detail/${unit.id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex h-6 w-6 items-center justify-center rounded-full border border-moss-primary text-moss-primary hover:bg-moss-primary/10 shrink-0"
                        title="Tijdslots beheren"
                      >
                        <Calendar size={14} weight="duotone" />
                      </a>
                      {unit.visibleInCalendar && (
                        <a
                          href={`https://yally.apartments/${unit.id}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex h-6 w-6 items-center justify-center rounded-full border border-moss-primary/40 text-moss-primary hover:bg-moss-primary/10 shrink-0"
                          title={`yally.apartments/${unit.id}`}
                        >
                          <ArrowSquareOut size={12} />
                        </a>
                      )}
                    </div>
                  </TableCell>

                  {/* Publicatie */}
                  <TableCell>
                    <PublicatieCell
                      unit={unit}
                      onImmowebChange={async (unitId, referenceId) => {
                        await handleImmowebReferenceUpdate(unitId, referenceId);
                      }}
                      onYallyChange={async (unitId, isPublished) => {
                        await handleUpdateUnit(unitId, {
                          rentIsPublishedOnYallyWebsite: isPublished,
                        });
                      }}
                    />
                  </TableCell>

                  {/* Makelaar */}
                  <TableCell>
                    <BrokerListCell
                      brokers={unit.brokerContacts}
                      pickerTitle={`Selecteer makelaar voor ${unit.id}`}
                      onChange={(next) => {
                        handleUpdateUnit(unit.id as string, {
                          brokerContacts: next,
                        } as any);
                      }}
                    />
                  </TableCell>

                  {/* Bezichtigingsverantwoordelijke */}
                  <TableCell>
                    <ContactLinkCell
                      contact={unit.visitationResponsibleContact}
                      emptyLabel="Geen contact"
                      pickerTitle={`Selecteer bezichtigingsverantwoordelijke voor ${unit.id}`}
                      onUpdate={(contactData) => {
                        handleUpdateUnit(unit.id as string, {
                          visitationResponsibleContact: contactData,
                        } as any);
                      }}
                    />
                  </TableCell>

                  {/* Geafficheerde huur */}
                  <TableCell>
                    <EditableCurrencyCell
                      value={unit.advertisedRentalPrice}
                      editLabel="Geafficheerde huur bewerken"
                      comparison={{
                        benchmark: unit.estimatedRentalValue,
                        benchmarkLabel: "ERV",
                        valueLabel: "Geafficheerde huur",
                      }}
                      onSave={(next) =>
                        handleUpdateUnit(unit.id as string, {
                          advertisedRentalPrice: next,
                        })
                      }
                    />
                  </TableCell>

                  {/* Geafficheerde provisies */}
                  <TableCell>
                    <EditableCurrencyCell
                      value={unit.advertisedProvisions}
                      editLabel="Geafficheerde provisies bewerken"
                      comparison={{
                        benchmark: unit.estimatedCommonCosts,
                        benchmarkLabel: "ERV provisies",
                        valueLabel: "Geafficheerde provisies",
                      }}
                      onSave={(next) =>
                        handleUpdateUnit(unit.id as string, {
                          advertisedProvisions: next,
                        })
                      }
                    />
                  </TableCell>

                  {/* ERV */}
                  <TableCell>
                    <EditableCurrencyCell
                      value={unit.estimatedRentalValue}
                      editLabel="ERV bewerken"
                      onSave={(next) =>
                        handleUpdateUnit(unit.id as string, {
                          estimatedRentalValue: next,
                        })
                      }
                    />
                  </TableCell>

                  {/* ERV/m² */}
                  <TableCell>
                    <span className="text-neutral-primary">{calculateERVPerM2(unit)}</span>
                  </TableCell>

                  {/* ERV provisies */}
                  <TableCell>
                    <EditableCurrencyCell
                      value={unit.estimatedCommonCosts}
                      editLabel="Provisies bewerken"
                      onSave={(next) =>
                        handleUpdateUnit(unit.id as string, {
                          estimatedCommonCosts: next,
                        })
                      }
                    />
                  </TableCell>

                  {/* Voorzieningen */}
                  <TableCell>
                    <div className="flex items-center gap-1.5">
                      <UnitCommercialInfoComponent
                        unit={unit}
                        onUnitUpdate={(updates: Partial<UnitDTO>) => handleCommercialInfoUpdate(unit.id as string, updates)}
                        isCompact={true}
                      />
                      <button
                        onClick={() => handleOpenCommercialInfoModal(unit)}
                        className="flex h-6 w-6 items-center justify-center rounded-full border border-moss-primary text-moss-primary hover:bg-moss-primary/10 transition-colors shrink-0"
                        title="Alle voorzieningen bekijken"
                      >
                        <Info size={14} weight="duotone" />
                      </button>
                    </div>
                  </TableCell>

                  {/* Renovatie */}
                  <TableCell>
                    <CustomToggle
                      isSelected={unit.vacantDueToRenovation || false}
                      onValueChange={(value: boolean) => {
                        handleUpdateUnit(unit.id as string, {
                          vacantDueToRenovation: value,
                        });
                      }}
                    />
                  </TableCell>
                </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </div>

      <TimeslotModal
        isOpen={isTimeslotModalOpen}
        onClose={handleCloseTimeslotModal}
        unit={selectedUnit}
        onUnitUpdate={(updates) => {
          if (selectedUnit?.id) {
            handleUnitUpdate(selectedUnit.id, updates);
          }
        }}
      />

      <Modal
        isOpen={isCommercialInfoModalOpen}
        onClose={handleCloseCommercialInfoModal}
        size="4xl"
        shouldBlockScroll={true}
        scrollBehavior="inside"
        backdrop="blur"
      >
        <ModalContent>
          <ModalHeader className="flex flex-row items-center justify-between">
            <h2 className="text-xl font-semibold">Unit voorzieningen - {selectedCommercialInfoUnit?.id}</h2>
          </ModalHeader>
          <ModalBody className="px-0">
            {selectedCommercialInfoUnit && (
              <UnitCommercialInfoComponent
                unit={selectedCommercialInfoUnit}
                onUnitUpdate={(updates) => handleCommercialInfoUpdate(selectedCommercialInfoUnit.id as string, updates)}
                isCompact={false}
              />
            )}
          </ModalBody>
        </ModalContent>
      </Modal>
    </div>
  );
};

export default UpcomingVacantUnitsTable;
